import { Link } from 'react-router-dom';
import MetaHead from '../../components/MetaHead';
import { DanmarkMiniCallout } from '../../components/SygeforsikringDanmark';

export default function OnlineTerapiPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      <MetaHead 
        title="Online terapi - Din Privat Psykolog"
        description="Online terapi via GDPR-sikret og dobbeltkrypteret videokonsultation. Fleksibel og fortrolig psykologhjælp ved autoriseret psykolog Marie Aagaard Larsen."
        ogTitle="Online terapi - Din Privat Psykolog"
        ogDescription="Online terapi via GDPR-sikret og dobbeltkrypteret videokonsultation. Fleksibel og fortrolig psykologhjælp ved autoriseret psykolog Marie Aagaard Larsen."
        ogImage="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/online-terapi-danish-clinic.jpg"
      />

      {/* Breadcrumb / Back Link */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-[#465A4F]">
        <Link to="/" className="hover:text-[#5f7365] underline font-medium">
          Forside
        </Link>
        <span>/</span>
        <span className="text-[#5f7365] font-semibold">Online terapi</span>
      </div>

      {/* Header */}
      <div className="border-b border-[#E0D7C6] pb-8 space-y-4">
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#5f7365]">
          Online terapi
        </h1>
        <p className="text-base sm:text-lg text-[#3B4D42] max-w-3xl leading-relaxed">
          Nemt og fleksibelt forløb fra din egen smartphone eller computer, uanset hvor i landet du befinder dig.
        </p>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-6">
          <div className="space-y-4 text-sm sm:text-base text-[#5f7365] leading-relaxed">
            <h2 className="font-serif text-xl sm:text-2xl font-bold text-[#5f7365]">
              Hvorfor vælge online terapi?
            </h2>
            <p>
              Online terapi giver dig mulighed for at modtage professionel og nærværende psykologbehandling uden at skulle bruge tid på transport eller omstilling. Det er en ideel løsning, hvis du har en travl hverdag, bor i en anden del af landet, har fysiske begrænsninger, eller blot bedst kan lide at sidde i trygge rammer i dit eget hjem.
            </p>

            <h3 className="font-serif text-lg font-bold text-[#5f7365] pt-2">
              Sådan foregår det teknisk
            </h3>
            <p>
              Jeg bruger en godkendt, GDPR-sikret og dobbeltkrypteret online platform til videokonsultationer.
            </p>
            <ul className="space-y-2 list-disc pl-5 text-[#384A3F]">
              <li>Du behøver ikke installere besværlige programmer.</li>
              <li>Før samtalen modtager du et direkte og nemt link på SMS og e-mail.</li>
              <li>Du klikker blot på linket fra din smartphone, tablet eller computer, når samtalen starter.</li>
            </ul>

            <div className="pt-2 space-y-2">
              <h4 className="font-serif text-base font-bold text-[#5f7365] flex items-center gap-2">
                <i className="fa-regular fa-shield-check text-[#5f7365]" aria-hidden="true" />
                Samme høje faglige kvalitet
              </h4>
              <p className="text-xs sm:text-sm text-[#465A4F] leading-relaxed">
                Effekten af online samtaleterapi svarer fuldstændigt til fysiske møder i klinikken. Du mødes med den samme nærværende og erfarne autoriserede psykolog hver gang.
              </p>
            </div>
          </div>

          <div className="pt-6 border-t border-[#EDE6D8] flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/kontakt"
              className="px-6 py-3.5 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <i className="fa-regular fa-calendar-check" aria-hidden="true" />
              <span>Book tid til online terapi</span>
            </Link>
            <Link
              to="/priser"
              className="px-5 py-3.5 bg-white hover:bg-[#FAF8F3] text-[#5f7365] border border-[#D5CEBF] font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <span>Se priser og tilskud</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* Right Info Box */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-[#5f7365] text-white p-6 space-y-5">
            <div className="overflow-hidden border border-[#4e6053]">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/online-terapi-danish-clinic.jpg"
                alt="Online terapi videokonsultation i trygge rammer"
                className="w-full h-56 object-cover"
              />
            </div>

            <h3 className="font-serif text-lg font-bold text-white">
              Fordele ved online terapi
            </h3>
            <ul className="space-y-3 text-xs sm:text-sm text-[#D5E3DB]">
              <li className="flex items-start gap-2.5">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
                <span>Ingen transporttid eller rejsetid.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
                <span>Fuld fleksibilitet – deltag hjemmefra eller fra kontoret.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
                <span>100% GDPR-sikret og dobbeltkrypteret videoforbindelse.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
                <span>Kan kombineres med fysiske møder efter ønske.</span>
              </li>
            </ul>

            <div className="pt-4 border-t border-[#4e6053] space-y-3">
              <Link
                to="/kontakt"
                className="w-full py-2.5 px-4 bg-[#FAF8F3] hover:bg-white text-[#5f7365] font-semibold rounded-none text-xs flex items-center justify-center gap-2 transition-all duration-200 hover:-translate-y-1"
              >
                <i className="fa-regular fa-envelope" aria-hidden="true" />
                Stil et spørgsmål om online terapi
              </Link>
            </div>
          </div>

          <DanmarkMiniCallout className="shadow-2xs" />
        </div>
      </div>
    </div>
  );
}
